<template>
  <Modal
    v-model:visible="isVisible"
    class="edit-quote-modal beta"
    :dismissable-mask="false"
    :header="header"
    :size="viewUplift ? 'lg' : 'md'"
    maximizable
    @keypress.enter="onSave"
  >
    <div class="flex flex-gap-1 flex-column">
      <div class="edit-quote-modal-carrier-profile">
        <div class="flex flex-column align-items-center justify-content-center">
          <CarrierAvatar
            v-if="internalValue.carrier"
            :carrier="internalValue.carrier"
            size="large"
          />
          <FontAwesomeIcon
            v-else
            class="carrier-icon text-5xl"
            :icon="['fas', 'truck']"
          />
          <h1 v-if="internalValue.carrier" class="carrier-name">
            {{ internalValue.carrier.name }}
            {{
              internalValue.carrier.scac?.length
                ? `(${internalValue.carrier.scac})`
                : ''
            }}
          </h1>
          <span v-else>
            {{ t('common.noParamSelected', { param: t('carriers.carriers') }) }}
          </span>
        </div>

        <div v-if="internalValue.carrier" class="carrier-info-row">
          <KeyValuePair
            :label="t('common.name')"
            :value="internalValue.carrier.name"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.scac')"
            :value="internalValue.carrier.scac"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.mcNumber')"
            :value="internalValue.carrier.mcNumber"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.dotNumber')"
            :value="internalValue.carrier.dotNumber"
            class="w-3"
          />
        </div>
      </div>

      <Message v-if="equipmentMismatch" severity="warn" class="rygen-message">
        {{
          t('rateShop.equipmentTypeMismatchDetails', {
            param:
              parentEntity instanceof Order
                ? t('orders.orders')
                : t('shipments.shipments'),
            quoteType: internalValue.equipmentType?.name,
            parentType: equipmentType?.name,
          })
        }}
      </Message>

      <form
        :aria-label="
          internalValue.source === 'SPOT_QUOTE'
            ? t('rateShop.spotQuotes')
            : t('rateShop.quotes')
        "
        class="edit-quote-modal-form flex flex-column flex-gap-1"
      >
        <div class="edit-quote-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-carrier"
            :label="t('carriers.carriers')"
            :errors="v$.carrier.$errors"
            class="w-4"
          >
            <CarrierQuickSearch
              :id="inputId"
              ref="firstInput"
              v-model="internalValue.carrier"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :search-type="showAllCarriers ? 'all' : 'assigned'"
              :disabled="isSpotMarketQuote"
              append-to="body"
              @change="onCarrierChange($event.value)"
              @clear="onCarrierChange()"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-mode"
            :label="t('shipments.modes')"
            :errors="v$.mode.$errors"
            class="w-4"
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.mode"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableModeOptions"
              :placeholder="t('shipments.modes')"
              append-to="body"
              option-label="label"
              option-value="value"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-service-level"
            :errors="v$.serviceType.$errors"
            :label="t('carriers.serviceLevels')"
            class="w-4"
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.serviceType"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableServiceLevels"
              :placeholder="t('carriers.serviceLevels')"
              append-to="body"
            />
          </InputWrapper>
        </div>

        <div class="edit-quote-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="edit-quote-modal-form-quote-id"
            :label="t('common.paramId', { param: t('rateShop.quotes') })"
            class="w-4"
            optional
          >
            <InputText
              :id="inputId"
              v-model="internalValue.carrierQuoteNumber"
              v-bind="ariaProps"
              :placeholder="
                t('common.paramId', { param: t('rateShop.quotes') })
              "
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="edit-quote-modal-form-transit-days"
            :label="t('rateShop.transitDays')"
            class="w-4"
            optional
          >
            <InputNumber
              v-model="internalValue.transitTime"
              v-bind="ariaProps"
              :input-id="inputId"
              :placeholder="t('rateShop.transitDays')"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-equipment-types"
            :label="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            :errors="v$.equipmentType.$errors"
            class="w-4"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.equipmentType"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableEquipmentTypes"
              :placeholder="
                t('common.paramTypes', { param: t('referenceData.equipment') })
              "
              option-label="name"
              data-key="id"
              append-to="body"
              filter
              show-clear
            />
          </InputWrapper>
        </div>

        <div v-if="shouldShowLaneQuoteOption" class="edit-quote-modal-form-row">
          <WrappedSwitch
            id="edit-quote-modal-form-show-lane-quote"
            v-model="showLaneQuoteEdit"
            :label="
              t('common.saveAsParam', { param: t('rateShop.laneQuotes') })
            "
          />
        </div>

        <EditLaneQuoteData
          v-if="showLaneQuoteEdit"
          ref="laneQuoteDataEdit"
          v-model="internalValue.laneQuoteData"
          v-model:origin-location-options="originLocationOptions"
          v-model:destination-location-options="destLocationOptions"
        />
      </form>

      <EditQuoteChargesSection
        v-model="chargeTableItems"
        v-model:currency-code="internalValue.currencyCode"
        :accessorial-codes="accessorialCodes.data"
        :stops="stops"
        :can-view-uplift="viewUplift"
      />
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        v-if="
          $hasAuthority('spotQuote', 'delete') && internalValue.id && deletable
        "
        mode="secondary"
        icon="pi pi-trash"
        :label="t('common.deleteParam', { param: t('rateShop.quotes') })"
        :severity="Severity.DANGER"
        @click="onDelete"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.saveParam', { param: t('rateShop.quotes') })"
        :disabled="equipmentMismatch"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import {
  computed,
  nextTick,
  onMounted,
  provide,
  ref,
  toRef,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  required,
  serverValidationPlaceholder,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  CapacityProviderType,
  CarrierSetting,
  DropdownOption,
  EquipmentType,
  Loadable,
  Mode,
  Severity,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  Carrier,
  EmbeddableLocation,
  Lane,
  LaneQuoteData,
  LaneQuoteLocationOption,
  Order,
  RateQuote,
  RateQuoteAccessorial,
  WellKnownService,
} from '@/model';
import {
  BaseEditQuoteChargeTableItem,
  EditQuoteClientChargeTableItem,
  EditQuoteThreeplChargeTableItem,
  fromClientChargeTableItems,
  fromThreeplChargeTableItems,
  toClientChargeTableItems,
  toThreeplChargeTableItems,
} from '@/model/rate/editQuote';
import { useAccessorialCodeStore } from '@/stores/AccessorialCode';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useParentEntityStore } from '@/stores/ParentEntity';

import CarrierQuickSearch from '@/components/carrier/CarrierQuickSearch.vue';
import EditLaneQuoteData from '@/components/rateShop/EditLaneQuoteData.vue';
import EditQuoteChargesSection from '@/components/rateShop/editQuote/EditQuoteChargesSection.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  viewUplift: {
    type: Boolean,
    default: () => false,
  },
  deletable: {
    type: Boolean,
    default: () => false,
  },
  equipmentType: {
    type: EquipmentType,
    default: undefined,
  },
});
const { modelValue, viewUplift, deletable, equipmentType } = toRefs(props);
const emit = defineEmits<{
  (event: 'save', rateQuote: RateQuote): void;
  (event: 'delete', rateQuote: RateQuote): void;
}>();

const firstInput = useTemplateRef('firstInput');
const laneQuoteDataEdit = useTemplateRef('laneQuoteDataEdit');

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const confirm = useConfirm();
const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const toast = useToast();
const uid = useUid('edit-quote-modal').uid;
provide('scope', uid);

const internalValue: Ref<RateQuote> = ref(new RateQuote());
const rules = {
  carrier: {
    scac: {
      required: required(t('carriers.carriers')),
    },
  },
  rawLineHaulNetCharge: {
    required: required(t('shipments.financials.linehaul')),
  },
  mode: {
    required: required(t('shipments.modes')),
  },
  serviceType: {
    required: required(t('carriers.serviceLevels')),
  },
  equipmentType: { serverValidationPlaceholder },
  effectiveDate: { serverValidationPlaceholder },
  expirationDate: { serverValidationPlaceholder },
  origin: { serverValidationPlaceholder },
  destination: { serverValidationPlaceholder },
};
const { externalResults, v$, validate } = useValidations(
  internalValue,
  rules,
  uid,
);

const clientChargeTableItems: Ref<EditQuoteClientChargeTableItem[]> = ref([]);
const threeplChargeTableItems: Ref<EditQuoteThreeplChargeTableItem[]> = ref([]);
const hasCarrierChanged = ref(false);
const showLaneQuoteEdit = ref(false);
const originLocationOptions: Ref<LaneQuoteLocationOption[]> = ref([]);
const destLocationOptions: Ref<LaneQuoteLocationOption[]> = ref([]);
const carrierSettings: Ref<Loadable<CarrierSetting>> = ref({ loading: false });

const stops = toRef(() => parentEntity.value?.stops ?? []);
const origin = toRef(() => stops.value[0].location);
const destination = toRef(() => stops.value[stops.value.length - 1].location);

const header = computed(() => {
  const param =
    internalValue.value.source === 'SPOT_QUOTE'
      ? t('rateShop.spotQuotes')
      : t('rateShop.quotes');
  return internalValue.value.id
    ? t('common.editParam', { param })
    : t('common.addParam', { param });
});

const chargeTableItems = computed({
  get: () =>
    viewUplift.value
      ? threeplChargeTableItems.value
      : clientChargeTableItems.value,
  set: (newVal: BaseEditQuoteChargeTableItem[]) => {
    if (viewUplift.value) {
      threeplChargeTableItems.value =
        newVal as EditQuoteThreeplChargeTableItem[];
    } else {
      clientChargeTableItems.value = newVal as EditQuoteClientChargeTableItem[];
    }
  },
});

const hasLaneQuotePermission = computed(() =>
  authorization.hasAuthority('laneQuote', 'create'),
);

const isSpotMarketQuote = computed(
  () => internalValue.value.source === 'SPOT_MARKET',
);

const showAllCarriers = computed(() =>
  authorization.hasAuthority('spotQuote', 'allCarriers'),
);

const availableModes: Ref<Mode[]> = computed(() => {
  let carrierSettingModes = carrierSettings.value.data?.modes || [];
  return carrierSettingModes.length > 0
    ? carrierSettingModes
    : Object.values(Mode);
});

const availableModeOptions: Ref<DropdownOption<Mode>[]> = computed(() => {
  return availableModes.value.map((mode) => ({
    label: t(`enumMap.Mode.${mode}`),
    value: mode,
  }));
});

const availableServiceLevels: Ref<string[]> = computed(() => {
  let carrierServiceLevels =
    carrierSettings.value?.data?.serviceLevels
      ?.map((x) => x.name)
      ?.filter(notNull) || [];
  return carrierServiceLevels.length > 0
    ? carrierServiceLevels
    : [WellKnownService.STANDARD];
});

const availableEquipmentTypes: Ref<EquipmentType[]> = computed(() => {
  const carrierEquipmentTypes =
    carrierSettings.value?.data?.equipmentTypes || [];
  return carrierEquipmentTypes.length > 0
    ? carrierEquipmentTypes
    : equipmentTypes.value.data || [];
});

const shouldShowLaneQuoteOption = computed(
  () =>
    hasLaneQuotePermission.value &&
    internalValue.value.source === 'SPOT_QUOTE' &&
    internalValue.value.id === undefined,
);

const totalAccessorialCharges = computed(() => {
  return internalValue.value.accessorials
    .map((a) => a.charge)
    .filter(notNull)
    .reduce((total, curr) => total + curr, 0);
});

const totalRate = computed(() => {
  const linehaul = internalValue.value.rawLineHaulGrossCharge ?? 0;
  const fuel = internalValue.value.fuelSurchargeAmount ?? 0;
  return linehaul + fuel + totalAccessorialCharges.value;
});

const equipmentMismatch = computed(() => {
  const hasEquipment =
    internalValue.value.equipmentType && equipmentType?.value;
  const hasMismatch =
    internalValue.value.equipmentType?.id !== equipmentType?.value?.id;
  return hasEquipment ? hasMismatch : false;
});

const setDefaultMode = (availableModes: Mode[]) => {
  if (!internalValue.value.mode && availableModes.length === 1) {
    internalValue.value.mode = availableModes[0];
  }
};

const setDefaultServiceLevel = (availableServiceLevels: string[]) => {
  if (!internalValue.value.serviceType && availableServiceLevels.length === 1) {
    internalValue.value.serviceType = availableServiceLevels[0];
  }
};

const onCarrierChange = async (carrier?: Carrier) => {
  hasCarrierChanged.value = true;
  if (!carrier) {
    carrierSettings.value.data = new CarrierSetting();
    return;
  }

  await loadCarrierSettings(carrier);
  applyCarrierInvoiceDefaultCurrency();
};

const loadCarrierSettings = async (carrier: Carrier) => {
  if (!carrier.scac || !authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    carrierSettings.value.loading = true;
    carrierSettings.value.data =
      carrier.type === CapacityProviderType.CARRIER
        ? await api.carrier.findCarrierSettingsForScac(carrier.scac)
        : await api.broker.findBrokerSettingsForScac(carrier.scac);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No CarrierSettings found but no need to show an error
      carrierSettings.value.data = new CarrierSetting();
      return;
    }
    console.error("Error loading selected carrier's settings", e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.carrierSettings', 2),
      }),
      error: e,
    });
  } finally {
    if (hasCarrierChanged.value) {
      await checkValidations();
    }
    carrierSettings.value.loading = false;
  }
};

const applyCarrierInvoiceDefaultCurrency = () => {
  if (!carrierSettings.value?.data?.defaultCurrency) {
    return;
  }

  const newCurrency = carrierSettings.value.data.defaultCurrency;
  if (newCurrency !== internalValue.value.currencyCode) {
    confirm.require({
      message: t('rateShop.confirmChangeCurrency', {
        currency: newCurrency,
        scac: internalValue.value.carrier?.scac,
      }),
      icon: 'pi pi-exclamation-triangle text-warning',
      group: 'rygen-confirm-dialog',
      header: t('rateShop.carrierDefaultCurrency'),
      accept: () => {
        internalValue.value.currencyCode = newCurrency;
      },
      acceptClass: 'p-button-sm p-button-warning',
      rejectClass: 'p-button-sm p-button-text',
    });
  }
};

const populateAccessorials = () => {
  const topLevelAccessorials =
    parentEntity.value?.requestedAccessorials.map(
      (code) => new RateQuoteAccessorial({ code }),
    ) ?? [];
  const stopLevelAccessorials = stops.value
    .map((s) => {
      const stopSequenceNumber = s.sequenceNumber;
      return s.requestedAccessorials.map(
        (code) => new RateQuoteAccessorial({ code, stopSequenceNumber }),
      );
    })
    .flat();

  internalValue.value.accessorials = [
    ...topLevelAccessorials,
    ...stopLevelAccessorials,
  ];

  if (!internalValue.value.id) {
    return;
  }

  internalValue.value.accessorials.forEach((a) => {
    const rateCharge = internalValue.value.rawCharges.find(
      (c) =>
        c.code === a.code?.naturalId &&
        c.stopSequenceNumber === a.stopSequenceNumber,
    );
    a.charge = rateCharge?.charge;
  });
};

const validateTotal = () => {
  if (totalRate.value >= 0) {
    return true;
  }

  externalResults.value = {
    ...externalResults.value,
    rawLineHaulGrossCharge: [t('rateShop.spotQuoteRateWarning')],
  };

  return false;
};

const validateValueIsAvailable = <T,>(
  value: T | undefined | null,
  availableValues: T[],
  dataKey?: keyof T,
): boolean => {
  if (!value) {
    // Ignore undefined fields. Use `required` if it should always be defined
    return true;
  }

  return dataKey
    ? availableValues.some((v) => v[dataKey] === value[dataKey])
    : availableValues.includes(value);
};

const validateOptions = () => {
  if (!hasCarrierChanged.value) {
    return true;
  }

  let isValid = true;

  if (
    !validateValueIsAvailable(internalValue.value.mode, availableModes.value)
  ) {
    isValid = false;
    externalResults.value = {
      ...externalResults.value,
      mode: [
        t('validations.editQuoteModal.oneOf', { label: t('shipments.modes') }),
      ],
    };
  }

  if (
    !validateValueIsAvailable(
      internalValue.value.serviceType,
      availableServiceLevels.value,
    )
  ) {
    isValid = false;
    externalResults.value = {
      ...externalResults.value,
      serviceType: [
        t('validations.editQuoteModal.oneOf', {
          label: t('carriers.serviceLevels'),
        }),
      ],
    };
  }

  if (
    !validateValueIsAvailable(
      internalValue.value.equipmentType,
      availableEquipmentTypes.value,
      'naturalId',
    )
  ) {
    isValid = false;
    externalResults.value = {
      ...externalResults.value,
      equipmentType: [
        t('validations.editQuoteModal.oneOf', {
          label: t('common.paramTypes', {
            param: t('referenceData.equipment'),
          }),
        }),
      ],
    };
  }

  return isValid;
};

const validateManualConditions = (): boolean => {
  // Don't chain these calls, `one && two`, so a failure in one doesn't prevent
  // successive validations from running
  const isTotalValid = validateTotal();
  const areOptionsValid = validateOptions();
  return isTotalValid && areOptionsValid;
};

const onDelete = () => {
  isVisible.value = false;
  emit('delete', internalValue.value);
};

const checkValidations = async () => {
  if (equipmentMismatch.value) {
    return;
  }

  if (showLaneQuoteEdit.value) {
    laneQuoteDataEdit.value?.clearExternalValidations();
  }

  const areAutoValidationsValid = await validate();
  const areManualValidationsValid = validateManualConditions();

  return areAutoValidationsValid && areManualValidationsValid;
};

const initializeEditQuoteChargeTableItems = () => {
  if (viewUplift.value) {
    threeplChargeTableItems.value = toThreeplChargeTableItems(
      internalValue.value,
    );
  } else {
    clientChargeTableItems.value = toClientChargeTableItems(
      internalValue.value,
    );
  }
};

const updateRateQuoteTableValues = () => {
  if (viewUplift.value) {
    internalValue.value = fromThreeplChargeTableItems(
      internalValue.value,
      threeplChargeTableItems.value,
    );
  } else {
    internalValue.value = fromClientChargeTableItems(
      internalValue.value,
      clientChargeTableItems.value,
    );
  }
};

const onSave = async () => {
  updateRateQuoteTableValues();

  const isValid = await checkValidations();
  if (isValid) {
    if (showLaneQuoteEdit.value) {
      try {
        if (!laneQuoteDataEdit.value?.validateOriginDestination()) {
          return;
        }

        addOriginAndDestinationInformation();
      } catch (e: any) {
        if (axios.isCancel(e)) {
          return;
        }

        toast.addError({
          detail: t('errors.savingParam', {
            param: t('rateShop.laneQuotes', 1),
          }),
          error: e,
        });
        return;
      }
    }

    isVisible.value = false;
    emit('save', internalValue.value);
  }
};

const addOriginAndDestinationInformation = () => {
  if (origin?.value === undefined || destination?.value === undefined) {
    throw new Error(
      'Origin or destination is undefined. Could not create lane quote',
    );
  }
  if (internalValue.value.laneQuoteData === undefined) {
    throw new Error('LaneQuoteData is undefined. Could not create lane quote');
  }
  const lane = new Lane({
    origin: new EmbeddableLocation(),
    destination: new EmbeddableLocation(),
  });
  originLocationOptions.value.forEach((v) =>
    v.apply(lane.origin, origin.value),
  );
  destLocationOptions.value.forEach((v) =>
    v.apply(lane.destination, destination.value),
  );

  internalValue.value.laneQuoteData.lane = lane;
};

watch(
  modelValue,
  (modelValue: RateQuote) => {
    internalValue.value = new RateQuote(modelValue);
    v$.value.$reset();
    hasCarrierChanged.value = false;

    nextTick(() => {
      setDefaultMode(availableModes.value);
      setDefaultServiceLevel(availableServiceLevels.value);
    });
  },
  { deep: true, immediate: true },
);

watch(
  isVisible,
  (newVal) => {
    v$.value.$reset();
    hasCarrierChanged.value = false;
    showLaneQuoteEdit.value = false;
    originLocationOptions.value = [];
    destLocationOptions.value = [];
    threeplChargeTableItems.value = [];
    clientChargeTableItems.value = [];

    if (newVal) {
      nextTick(() => {
        const el = firstInput.value?.$el as HTMLElement | null;
        const input = el?.querySelector(
          'input,select,textarea',
        ) as HTMLElement | null;
        input?.focus();
      });

      if (!modelValue.value.id) {
        populateAccessorials();
      }

      loadRequestableAccessorialCodes();
      loadEquipmentTypes();
      initializeEditQuoteChargeTableItems();

      if (internalValue.value.carrier) {
        loadCarrierSettings(internalValue.value.carrier);
      }
    }
  },
  { immediate: true },
);

watch(availableModes, (newVal: Mode[]) => {
  setDefaultMode(newVal);
});

watch(availableServiceLevels, (newVal: string[]) => {
  setDefaultServiceLevel(newVal);
});

watch(showLaneQuoteEdit, (newVal: boolean) => {
  if (newVal) {
    internalValue.value.laneQuoteData = new LaneQuoteData();
  } else {
    internalValue.value.laneQuoteData = undefined;
  }
});

/**
 * Handles if modal is opened pre-maturely and chargeTable initializes with `viewUplift` as false (incorrectly).
 * Shouldn't happen with the `disabled` guard on the `+ Add Quote` primary action, but this component shouldn't
 * have to rely on that
 */
watch(viewUplift, () => {
  if (isVisible.value) {
    initializeEditQuoteChargeTableItems();
  }
});

onMounted(() => {
  loadEquipmentTypes();
});
</script>

<style>
.edit-quote-modal.beta {
  .edit-quote-modal-carrier-profile {
    margin-bottom: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);

    .carrier-icon {
      margin-bottom: var(--sp-3);
    }

    .carrier-name {
      margin-bottom: var(--sp-6);
    }

    .carrier-info-row {
      display: flex;
      gap: var(--sp-3);
      justify-content: center;
    }
  }

  .p-message {
    margin: 0;
  }

  .edit-quote-modal-form {
    overflow: auto;
    scroll-padding: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);

    .edit-quote-modal-form-row {
      display: flex;
      gap: var(--sp-3);

      &.accessorial-row {
        margin-top: var(--sp-3);

        .rate-quote-accessorials {
          margin-top: var(--sp-1);
        }
      }
    }
  }
}
</style>
